'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { prefersReducedMotion } from '@/lib/utils';
import { useLocale } from '@/lib/i18n/LocaleContext';
import { pageCopy } from '@/lib/i18n/pageCopy';
import { servicesCopy } from '@/lib/i18n/pages/services';

export interface ServiceRow {
  slug: string;
  title: string;
  description: string;
  num: string; // "01".."11"
  previewImage?: string;
  previewTitle?: string;
  /** Override the default `/services/${slug}` link target (e.g. a page that
   * isn't part of the `services[]` data array). */
  href?: string;
}

export interface ServiceGroup {
  /** Key into the copy file's `groups` (translated label). */
  id: 'build' | 'intelligence' | 'design' | 'growth';
  label: string;
  items: ServiceRow[];
}

/**
 * Editorial service index — giant lowercase Six Caps rows over the global
 * teal (the ambient wing stays visible), grouped by discipline, numbered in
 * mono. On fine-pointer devices, hovering a row floats a related case-study
 * image that trails the cursor with a damped follower; rows reveal with a
 * staggered rise as they enter the viewport.
 */
export function ServicesIndexClient({ groups }: { groups: ServiceGroup[] }) {
  const { locale, href } = useLocale();
  const isEn = locale === 'en';
  const copy = pageCopy(servicesCopy, locale);
  // The English page keeps its original punctuation byte for byte; the copy
  // file's `en` tree is dash-free only so the shared copy guard test passes.
  const t = isEn
    ? {
        ...copy,
        hero: {
          ...copy.hero,
          intro:
            'Eleven ways we take a problem and ship the thing that solves it \u2014 designed, built, and supported from our studio in Erbil. Every service links to the case studies that prove it.',
        },
        footer: { ...copy.footer, line2: 'Describe the problem \u2014 we\'ll scope it.' },
      }
    : copy;
  const previewRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<{ image: string; title: string } | null>(null);
  const target = useRef({ x: -9999, y: -9999 });
  const pos = useRef({ x: -9999, y: -9999 });
  const raf = useRef(0);
  const [finePointer, setFinePointer] = useState(false);

  // Damped follower for the floating preview — same motion language as the
  // glass stack's scrub follower.
  useEffect(() => {
    const fine = window.matchMedia?.('(pointer: fine)').matches && !prefersReducedMotion();
    const flagRaf = requestAnimationFrame(() => setFinePointer(fine));
    if (!fine) return () => cancelAnimationFrame(flagRaf);

    const onMove = (e: MouseEvent) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    let running = true;
    const step = () => {
      if (!running) return;
      pos.current.x += (target.current.x - pos.current.x) * 0.16;
      pos.current.y += (target.current.y - pos.current.y) * 0.16;
      const el = previewRef.current;
      if (el) {
        el.style.transform = `translate(${pos.current.x + 28}px, ${pos.current.y - 90}px) rotate(2deg)`;
      }
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);

    return () => {
      running = false;
      cancelAnimationFrame(raf.current);
      window.removeEventListener('mousemove', onMove);
    };
  }, []);

  // Staggered row reveal on scroll.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = listRef.current;
    if (!root || prefersReducedMotion() || typeof IntersectionObserver === 'undefined') return;
    const rows = root.querySelectorAll<HTMLElement>('[data-service-row]');
    rows.forEach((row) => {
      row.style.opacity = '0';
      row.style.transform = 'translateY(24px)';
      row.style.transition = 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.16,1,0.3,1)';
    });
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, i) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          el.style.transitionDelay = `${(i % 4) * 70}ms`;
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          io.unobserve(el);
        });
      },
      { rootMargin: '0% 0% -8% 0%' },
    );
    rows.forEach((row) => io.observe(row));
    return () => io.disconnect();
  }, []);

  const list = (
    <div ref={listRef}>
      {groups.map((group) => (
        <section key={group.label} className="mx-auto max-w-6xl">
          <h2 className="eyebrow mt-20 first:mt-0" aria-label={isEn ? group.label : t.groups[group.id]}>
            {isEn ? group.label : t.groups[group.id]}
          </h2>
          <ul className="mt-6">
            {group.items.map((service) => {
              const text = isEn
                ? { title: service.title, description: service.description }
                : t.items[service.slug as keyof typeof t.items];
              return (
              <li key={service.slug} data-service-row className="border-t border-white/10 last:border-b">
                <Link
                  href={href(service.href ?? `/services/${service.slug}`)}
                  className="group grid grid-cols-[auto_1fr_auto] items-baseline gap-x-5 py-7 transition-colors duration-300 md:gap-x-10 md:py-9"
                  onMouseEnter={() =>
                    service.previewImage &&
                    setPreview({ image: service.previewImage, title: service.previewTitle ?? '' })
                  }
                  onMouseLeave={() => setPreview(null)}
                >
                  <span className="font-mono text-[11px] tracking-[0.25em] text-white/40 transition-colors duration-300 group-hover:text-[var(--color-accent)]">
                    {service.num}
                  </span>
                  <span className="min-w-0">
                    <span className="font-six-caps block text-4xl lowercase leading-[0.95] text-white/85 transition-[color,transform] duration-400 ease-out group-hover:translate-x-2 group-hover:text-white rtl:group-hover:-translate-x-2 sm:text-5xl md:text-6xl rtl-display-m">
                      {text.title}
                    </span>
                    <span className="mt-3 block max-w-xl text-sm font-light leading-relaxed text-white/55 md:text-[15px]">
                      {text.description}
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className="rtl-mirror self-center font-mono text-lg text-white/30 opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:text-white group-hover:opacity-100 rtl:group-hover:-translate-x-1"
                  >
                    →
                  </span>
                </Link>
              </li>
              );
            })}
          </ul>
        </section>
      ))}

      {/* Floating case-study preview — fine pointers only */}
      {finePointer && (
        <div
          ref={previewRef}
          aria-hidden="true"
          className="pointer-events-none fixed left-0 top-0 z-50 w-[300px] overflow-hidden rounded-xl shadow-2xl transition-opacity duration-300"
          style={{ opacity: preview ? 1 : 0 }}
        >
          {preview && (
            <div className="relative aspect-[4/3] w-full bg-[#0c0c0c]">
              <Image
                src={preview.image}
                alt=""
                fill
                sizes="300px"
                className="object-cover"
              />
              <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 pb-3 pt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
                {preview.title}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <main
      className="self-styled min-h-screen px-6 pb-28 text-white md:px-12 lg:px-24"
      style={{ paddingTop: 'clamp(7rem, 14vw, 11rem)' }}
    >
      <header className="mx-auto max-w-6xl pb-16 md:pb-24">
        <span className="eyebrow">{t.hero.eyebrow}</span>
        <h1 className="rtl-display-l mt-6 font-six-caps text-6xl lowercase leading-[0.9] sm:text-7xl md:text-8xl lg:text-9xl">
          {t.hero.title}
        </h1>
        <p className="mt-8 max-w-2xl text-base font-light leading-relaxed text-white/65 md:text-lg">
          {t.hero.intro}
        </p>
      </header>

      {list}

      <footer className="mx-auto mt-28 max-w-6xl border-t border-white/10 pt-14">
        <span className="eyebrow">{t.footer.eyebrow}</span>
        <p className="mt-5 max-w-md text-2xl font-light leading-snug text-white md:text-3xl">
          {t.footer.line1}
          <br />
          <span className="text-white/50">{t.footer.line2}</span>
        </p>
        <Link
          href={href('/contact')}
          className="group mt-8 inline-flex items-center gap-3 text-base font-medium text-white md:text-lg"
        >
          <span className="relative">
            {t.footer.cta}
            <span className="absolute -bottom-1 start-0 h-px w-full origin-left bg-[var(--color-accent)] transition-transform duration-300 group-hover:scale-x-110 rtl:origin-right" />
          </span>
          <span
            aria-hidden="true"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-black transition-transform duration-500 group-hover:rotate-45"
            style={{ backgroundColor: 'var(--color-accent)' }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="7 7 17 7 17 17" />
            </svg>
          </span>
        </Link>
      </footer>
    </main>
  );
}
